import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Bell, AlertCircle, CheckCircle, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WebSocketContext = createContext(null);

const TOAST_ICONS = {
  chamar_gestor: AlertTriangle,
  contagem: AlertCircle,
  anomalia: AlertTriangle,
  alerta: AlertTriangle,
  sistema: Bell,
  info: Info,
  sucesso: CheckCircle,
  aviso: AlertCircle,
};

const TOAST_STYLES = {
  chamar_gestor: { bg: 'bg-red-50 border-red-300', icon: 'text-red-500' },
  contagem: { bg: 'bg-amber-50 border-amber-300', icon: 'text-amber-600' },
  anomalia: { bg: 'bg-red-50 border-red-200', icon: 'text-red-500' },
  alerta: { bg: 'bg-red-50 border-red-200', icon: 'text-red-500' },
  sistema: { bg: 'bg-blue-50 border-blue-300', icon: 'text-blue-500' },
  info: { bg: 'bg-blue-50 border-blue-200', icon: 'text-blue-500' },
  sucesso: { bg: 'bg-green-50 border-green-300', icon: 'text-green-500' },
  aviso: { bg: 'bg-amber-50 border-amber-200', icon: 'text-amber-500' },
};

export function WebSocketProvider({ children, userId, perfil = 'operador' }) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pollTimerRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [mode, setMode] = useState('connecting'); // 'ws' | 'polling' | 'connecting'
  const modeRef = useRef('connecting');
  const [lastNotification, setLastNotification] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const mountedRef = useRef(true);
  const failCountRef = useRef(0);
  const MAX_WS_FAILURES = 1; // Mudar para polling após 1 falha (rápido em Kubernetes)

  const setModeSafe = useCallback((next) => {
    modeRef.current = next;
    setMode(next);
  }, []);

  const handleNotification = useCallback((data) => {
    if (!mountedRef.current) return;
    setLastNotification(data);
    setUnreadCount(prev => prev + 1);
    const style = TOAST_STYLES[data.tipo] || TOAST_STYLES.info;
    const Icon = TOAST_ICONS[data.tipo] || Bell;
    toast(data.titulo, {
      description: data.mensagem,
      duration: 6000,
      icon: React.createElement(Icon, { className: `h-5 w-5 ${style.icon}` }),
      className: `${style.bg} border`,
    });
  }, []);

  // Polling fallback: fetch notificações via HTTP
  const pollNotifications = useCallback(async () => {
    if (!BACKEND_URL || !userId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/notificacoes/recentes?limite=5`, { credentials: 'include' });
      if (res.ok) {
        if (mountedRef.current && modeRef.current === 'polling') {
          setConnected(true);
        }
      }
    } catch (e) {
      // Network error
    }
  }, [userId]);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(pollNotifications, 30000); // 30s
    pollNotifications(); // immediate first poll
  }, [pollNotifications]);

  const connect = useCallback(() => {
    if (!userId) return;
    // Se já estamos em polling mode, não tentar mais WebSockets
    if (modeRef.current === 'polling') return;

    // Build WS URL from HTTP URL (fallback para same-origin quando BACKEND_URL vazio)
    const wsBase = BACKEND_URL || window.location.origin;
    const wsProtocol = wsBase.startsWith('https') ? 'wss' : 'ws';
    const wsHost = wsBase.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/notifications/${userId}?perfil=${perfil}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Timeout: if connection doesn't open in 5s, close and fallback
      const openTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(openTimeout);
        if (mountedRef.current) {
          failCountRef.current = 0;
          setConnected(true);
          setModeSafe('ws');
          // Stop polling if we're on WS now
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          console.log('[WS] Conectado');
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.event === 'notification') {
            handleNotification(data);
          }
        } catch (e) {
          // ignore non-JSON messages (pong, etc.)
        }
      };

      ws.onclose = () => {
        clearTimeout(openTimeout);
        if (!mountedRef.current) return;
        failCountRef.current += 1;

        if (failCountRef.current >= MAX_WS_FAILURES) {
          // Switch to polling mode after repeated failures
          console.log('[WS] Falhou', failCountRef.current, 'vezes. A usar polling.');
          setModeSafe('polling');
          setConnected(true); // polling still works
          startPolling();
        } else {
          setConnected(false);
          setModeSafe('connecting');
          // Reconnect with exponential backoff
          const delay = Math.min(5000 * Math.pow(2, failCountRef.current - 1), 30000);
          reconnectTimer.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        clearTimeout(openTimeout);
        ws.close();
      };

      // Keepalive ping every 25s (below typical 30s proxy timeout)
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);

      ws._pingInterval = pingInterval;
    } catch (e) {
      console.error('[WS] Erro ao conectar:', e);
      failCountRef.current += 1;
      if (failCountRef.current >= MAX_WS_FAILURES) {
        setModeSafe('polling');
        setConnected(true);
        startPolling();
      } else {
        reconnectTimer.current = setTimeout(connect, 5000);
      }
    }
  }, [userId, perfil, handleNotification, startPolling, setModeSafe]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);

  const resetUnread = useCallback(() => setUnreadCount(0), []);

  const value = {
    connected,
    mode, // 'ws' | 'polling' | 'connecting'
    lastNotification,
    unreadCount,
    resetUnread,
  };

  return (
    React.createElement(WebSocketContext.Provider, { value }, children)
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext) || { connected: false, mode: 'connecting', lastNotification: null, unreadCount: 0, resetUnread: () => {} };
}
